/**
 * File system types for frontend
 */

export interface FileMetadata {
  clientId?: string;
  loanId?: string;
  formId?: string;
  uploadedVia?: 'web' | 'api' | 'mobile' | 'email';
}

export interface FileClassification {
  category: 'document' | 'financial' | 'legal' | 'identity' | 'tax' | 'bank_statement' | 'invoice' | 'contract' | 'other';
  confidence: number;
  autoClassified: boolean;
}

export interface FileOCR {
  text: string;
  confidence: number;
  processedAt: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
}

export interface SharePermission {
  userId: string;
  permission: 'read' | 'write' | 'admin';
  sharedAt: string;
  sharedBy: string;
}

export interface PublicLink {
  enabled: boolean;
  token?: string;
  expiresAt?: string;
  createdAt?: string;
}

export interface FileSharing {
  isShared: boolean;
  sharedWith: SharePermission[];
  publicLink?: PublicLink;
}

export interface AccessPermission {
  type: 'user' | 'role' | 'organization';
  id: string;
  permission: 'read' | 'write' | 'admin';
}

export interface FilePermissions {
  owner: string;
  organizationId?: string;
  access: AccessPermission[];
}

export interface FileVersion {
  versionNumber: number;
  path: string;
  size: number;
  checksum: string;
  createdAt: string;
  createdBy: string;
}

export interface File {
  id: string;
  name: string;
  originalName: string;
  path: string;
  mimeType: string;
  size: number;
  folderId?: string;
  type: 'file' | 'folder';
  tags: string[];
  classification?: FileClassification;
  ocr?: FileOCR;
  sharing?: FileSharing;
  metadata?: FileMetadata;
  permissions: FilePermissions;
  versions?: FileVersion[];
  isDeleted: boolean;
  deletedAt?: string;
  deletedBy?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy?: string;
}

// Request/Response types
export interface CreateFileRequest {
  name: string;
  originalName: string;
  mimeType: string;
  size: number;
  folderId?: string;
  tags?: string[];
  metadata?: FileMetadata;
}

export interface CreateFolderRequest {
  name: string;
  parentId?: string;
  metadata?: FileMetadata;
}

export interface UpdateFileRequest {
  name?: string;
  tags?: string[];
  classification?: FileClassification;
  metadata?: FileMetadata;
  ocr?: FileOCR;
}

export interface ShareFileRequest {
  userIds: string[];
  permission: 'read' | 'write' | 'admin';
}

export interface CreatePublicLinkRequest {
  expiresAt?: string;
}

export interface FileSearchRequest {
  query?: string;
  folderId?: string;
  tags?: string[];
  classification?: string;
  mimeType?: string;
  createdBy?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}

export interface FileUploadResponse {
  file: File;
  uploadUrl?: string;
}

export interface FileListResponse {
  files: File[];
  total: number;
  hasMore: boolean;
}

export interface FileBreadcrumb {
  id: string;
  name: string;
  type: 'folder';
}

export interface FileTreeNode {
  id: string;
  name: string;
  type: 'file' | 'folder';
  children?: FileTreeNode[];
  size?: number;
  mimeType?: string;
  createdAt: string;
}

// UI-specific types
export interface FileGridViewOptions {
  showThumbnails: boolean;
  gridSize: 'small' | 'medium' | 'large';
  sortBy: 'name' | 'date' | 'size' | 'type';
  sortOrder: 'asc' | 'desc';
}

export interface FileListViewOptions {
  showDetails: boolean;
  columns: FileColumn[];
  sortBy: 'name' | 'date' | 'size' | 'type';
  sortOrder: 'asc' | 'desc';
}

export interface FileColumn {
  id: string;
  label: string;
  visible: boolean;
  width?: number;
}

export interface FileExplorerState {
  currentFolderId?: string;
  selectedFiles: string[];
  viewMode: 'list' | 'grid' | 'tree';
  gridOptions: FileGridViewOptions;
  listOptions: FileListViewOptions;
  searchQuery: string;
  filters: FileFilters;
  isLoading: boolean;
  error?: string;
}

export interface FileFilters {
  type?: 'file' | 'folder';
  mimeType?: string;
  classification?: string;
  tags?: string[];
  dateRange?: {
    from: string;
    to: string;
  };
  sizeRange?: {
    min: number;
    max: number;
  };
  shared?: boolean;
}

// File upload types
export interface FileUploadItem {
  id: string;
  file: File;
  name: string;
  size: number;
  mimeType: string;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
  folderId?: string;
}

export interface FileUploadOptions {
  folderId?: string;
  tags?: string[];
  metadata?: FileMetadata;
  autoClassify?: boolean;
  autoOCR?: boolean;
}

// File context menu actions
export interface FileAction {
  id: string;
  label: string;
  icon: string;
  disabled?: boolean;
  onClick: (file: File) => void;
  divider?: boolean;
}

// File preview types
export interface FilePreview {
  type: 'image' | 'pdf' | 'text' | 'video' | 'audio' | 'unsupported';
  url?: string;
  content?: string;
  thumbnail?: string;
}

// Constants
export const SUPPORTED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'text/plain',
  'text/csv',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/json',
  'application/xml'
];

export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
export const MAX_FILES_PER_FOLDER = 1000;
export const MAX_FOLDER_DEPTH = 10;

export const FILE_CATEGORIES = [
  { value: 'document', label: 'Document', color: '#2196f3' },
  { value: 'financial', label: 'Financial', color: '#4caf50' },
  { value: 'legal', label: 'Legal', color: '#ff9800' },
  { value: 'identity', label: 'Identity', color: '#9c27b0' },
  { value: 'tax', label: 'Tax', color: '#f44336' },
  { value: 'bank_statement', label: 'Bank Statement', color: '#00bcd4' },
  { value: 'invoice', label: 'Invoice', color: '#795548' },
  { value: 'contract', label: 'Contract', color: '#607d8b' },
  { value: 'other', label: 'Other', color: '#9e9e9e' }
] as const;

export const MIME_TYPE_ICONS: Record<string, string> = {
  'application/pdf': 'PictureAsPdf',
  'image/jpeg': 'Image',
  'image/png': 'Image',
  'image/gif': 'Image',
  'text/plain': 'Description',
  'text/csv': 'TableChart',
  'application/msword': 'Description',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Description',
  'application/vnd.ms-excel': 'TableChart',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'TableChart',
  'application/json': 'Code',
  'application/xml': 'Code',
  'application/folder': 'Folder'
};

export const FILE_SIZE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = bytes / Math.pow(k, i);
  
  return `${size.toFixed(1)} ${FILE_SIZE_UNITS[i]}`;
}

export function getFileIcon(file: File): string {
  if (file.type === 'folder') {
    return 'Folder';
  }
  return MIME_TYPE_ICONS[file.mimeType] || 'InsertDriveFile';
}

export function getClassificationColor(category?: string): string {
  const categoryData = FILE_CATEGORIES.find(c => c.value === category);
  return categoryData?.color || '#9e9e9e';
}

export function isImageFile(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

export function isPdfFile(mimeType: string): boolean {
  return mimeType === 'application/pdf';
}

export function isTextFile(mimeType: string): boolean {
  return mimeType.startsWith('text/') || 
         mimeType === 'application/json' || 
         mimeType === 'application/xml';
}